import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { EditModal } from '@nucleus-commerce/admin-kit/forms';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useSkuHistory } from '../hooks/use-sku-history';

export interface SkuHistoryModalProps {
  productId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function HistoryBody({ productId }: { productId: string }) {
  const { t } = useTranslation('products');
  const format = useFormat();
  const query = useSkuHistory(productId);

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-9" />
        ))}
      </div>
    );
  }
  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  }
  if (query.data.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('sku.history.empty')}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('sku.history.columns.value')}</TableHead>
            <TableHead>{t('sku.history.columns.kind')}</TableHead>
            <TableHead>{t('sku.history.columns.reason')}</TableHead>
            <TableHead>{t('sku.history.columns.replacedAt')}</TableHead>
            <TableHead>{t('sku.history.columns.replacedBy')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.data.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-mono text-xs">{item.value}</TableCell>
              <TableCell>
                <Badge variant="outline" className="font-normal">
                  {t(`sku.history.kind.${item.kind}`)}
                </Badge>
              </TableCell>
              <TableCell>{t(`sku.history.reason.${item.reason}`)}</TableCell>
              <TableCell>
                <span title={format.utc(item.replacedAt)}>{format.dateTime(item.replacedAt)}</span>
              </TableCell>
              <TableCell>{item.replacedBy?.name ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Retired style numbers and variant SKUs of the product: what they were, why they went, when, by whom. */
export function SkuHistoryModal({ productId, open, onOpenChange }: SkuHistoryModalProps) {
  const { t } = useTranslation(['products', 'common']);
  return (
    <EditModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('products:sku.history.title')}
      description={t('products:sku.history.description')}
      footer={
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common:actions.close')}
        </Button>
      }
    >
      {open ? <HistoryBody productId={productId} /> : null}
    </EditModal>
  );
}
